// src/studio/components/DocumentWorkspace.jsx
//
// The one component the redesign plan centers on: driven entirely by
// GET /rds/documents/{type}/sections, so every controlled Jade document —
// Requirements first, then the HLD/SFDC Solution Design and Technical
// Design as they move onto the generic document-run model in later phases —
// is this same component with a different `documentType`. A template
// gaining, losing or renaming a section is a backend-only change; nothing
// here has a hardcoded section list (SPEC-07 D-7).
//
// Phase 2 scope: generate + view (prose and table sections — the
// Requirements Document has no diagram sections). Per-section
// regenerate/edit/version-history return once document_run_controller.py
// grows those routes (see the redesign plan's Phase 1 "not yet" note) —
// they are Phase 5 work, not missing by oversight.
import { useCallback, useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import PropTypes from "prop-types";
import {
  FileText, ListChecks, BookOpen, Users, Info, ShieldAlert,
  LogIn, LogOut, Table2, Layers, Puzzle, Plug, Cpu, Wand2, Loader2,
  CheckCircle2, RefreshCw, AlertCircle, GitBranch, Database, Server,
  Lock, HardDrive, Monitor, Package, ShieldCheck, ListTree, Download,
} from "lucide-react";
import { Button, Alert, SkeletonText, SkeletonCard } from "../../shared/ui";
import { studioDocumentsApi } from "../api/studioApi";
import ArtifactEditor from "./ArtifactEditor";
import MarkdownView from "./MarkdownView";
import DocumentPreviewModal from "./DocumentPreviewModal";
import { Eye } from "lucide-react";
import TableSectionView from "./TableSectionView";
import MermaidView from "./MermaidView";
import GroupSectionView from "./GroupSectionView";

const ICON_BY_KEY = {
  // Requirements Document
  purpose: Info, scope: ListChecks, project_overview: BookOpen,
  intended_audience: Users, assumptions_constraints_dependencies: ShieldAlert,
  glossary: BookOpen, references: BookOpen, inputs: LogIn, outputs: LogOut,
  captured_requirements: Table2, nonfunctional_requirements: Layers,
  contractual_requirements: FileText, interface_requirements: Plug,
  technical_requirements: Cpu, other_requirements: Puzzle,
  // High Level Design
  reference: BookOpen, solution_overview: Layers, hld_options: ListTree,
  selection_criteria: ListChecks, selected_approach: CheckCircle2,
  hld_diagram: GitBranch, assumptions: Info, constraints: ShieldAlert,
  dependencies: Plug, prerequisites: ListChecks, build_buy_reuse: Package,
  database_architecture: Database, network: GitBranch, servers: Server,
  hardware_requirements: HardDrive, operating_system_requirements: Monitor,
  software_requirements: Package, security_architecture: ShieldCheck,
  // SFDC Solution Design
  overview: Layers, requirement_designs: ListTree,
};

const ACTIVE_STATUSES = new Set(["pending", "generating"]);
const POLL_INTERVAL_MS = 6000;

function artifactFor(run, sectionKey) {
  return run?.artifacts?.find((a) => a.section_type === sectionKey) || null;
}

// A GROUP section is several artifacts sharing one key (see
// GroupSectionView.jsx) — sorted by group_index so items render in the
// order they were generated.
function groupArtifactsFor(run, sectionKey) {
  return (run?.artifacts || [])
    .filter((a) => a.section_type === sectionKey && a.group_key)
    .sort((a, b) => (a.group_index ?? 0) - (b.group_index ?? 0));
}

function hasContent(artifact) {
  return Boolean(artifact?.content_markdown && artifact.content_markdown.trim());
}

export default function DocumentWorkspace({
  projectId, documentType, sowId, title, description,
  sourceDocumentType, sourceDocumentLabel,
}) {
  const [sections, setSections] = useState([]);
  const [sectionsLoading, setSectionsLoading] = useState(true);
  const [run, setRun] = useState(null);
  const [runLoading, setRunLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [activeKey, setActiveKey] = useState(null);
  const [error, setError] = useState(null);
  // The prerequisite document's own latest run, when this document reads one
  // (the pipeline chain — SPEC-07's ApplicableDocument.depends_on). null once
  // resolved and there genuinely is no dependency; undefined while loading.
  const [sourceRun, setSourceRun] = useState(sourceDocumentType ? undefined : null);

  const pollRef = useRef(null);
  const prevStatusRef = useRef(null);
  // StudioProjectLayout fetches each stage's StageRail status once on mount
  // and never re-polls it (see its own load()) -- reloadProject is the hook
  // it exposes for a stage to say "something changed, re-check me" so the
  // rail's spinner doesn't keep spinning long after this run actually
  // finished. useOutletContext() can be undefined outside a route with that
  // context (e.g. in isolated tests), hence the fallback.
  const { reloadProject } = useOutletContext() || {};

  useEffect(() => {
    let cancelled = false;
    setSectionsLoading(true);
    studioDocumentsApi
      .sections(documentType)
      .then((data) => {
        if (cancelled) return;
        setSections(data);
        setActiveKey((current) => current ?? data[0]?.key ?? null);
      })
      .catch(() => { if (!cancelled) setError("Failed to load this document's section list."); })
      .finally(() => { if (!cancelled) setSectionsLoading(false); });
    return () => { cancelled = true; };
  }, [documentType]);

  const loadLatestRun = useCallback(async () => {
    setRunLoading(true);
    try {
      const latest = await studioDocumentsApi.latestRun(projectId, documentType);
      setRun(latest);
      return latest;
    } catch {
      setError("Failed to load generation status.");
      return null;
    } finally {
      setRunLoading(false);
    }
  }, [projectId, documentType]);

  useEffect(() => { loadLatestRun(); }, [loadLatestRun]);

  useEffect(() => {
    if (!sourceDocumentType) { setSourceRun(null); return; }
    let cancelled = false;
    studioDocumentsApi
      .latestRun(projectId, sourceDocumentType)
      .then((latest) => { if (!cancelled) setSourceRun(latest); })
      .catch(() => { if (!cancelled) setSourceRun(null); });
    return () => { cancelled = true; };
  }, [projectId, sourceDocumentType]);

  // Poll while a run is in flight; stop as soon as it reaches a terminal
  // state (APP-008 — the backend always reaches one, this just reflects it).
  useEffect(() => {
    if (!run || !ACTIVE_STATUSES.has(run.status)) {
      clearInterval(pollRef.current);
      return undefined;
    }
    pollRef.current = setInterval(async () => {
      try {
        const updated = await studioDocumentsApi.getRun(run.id);
        setRun(updated);
      } catch {
        clearInterval(pollRef.current);
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [run]);

  // Tells StudioProjectLayout's StageRail to re-check this stage's status as
  // soon as this run leaves an active state, so its spinner clears without
  // a manual page reload. Separate from the polling effect above so a
  // failed getRun() there (which stops the interval but leaves `run` as the
  // last-known active status) doesn't also fire a reload -- this only fires
  // on a real observed transition.
  useEffect(() => {
    const prev = prevStatusRef.current;
    const current = run?.status ?? null;
    if (prev && ACTIVE_STATUSES.has(prev) && current && !ACTIVE_STATUSES.has(current)) {
      reloadProject?.();
    }
    prevStatusRef.current = current;
  }, [run, reloadProject]);

  async function handleGenerate() {
    setStarting(true);
    setError(null);
    try {
      const kickoff = await studioDocumentsApi.startRun(projectId, documentType, {
        sowId,
        sourceRunId: sourceDocumentType ? sourceRun?.id : undefined,
      });
      const fresh = await studioDocumentsApi.getRun(kickoff.document_run_id);
      setRun(fresh);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to start generation.");
    } finally {
      setStarting(false);
    }
  }

  // Merges a saved artifact (manual edit, or an accepted "Ask AI"
  // suggestion) back into the current run's artifact list, so the section
  // rail and content pane reflect it without a full re-poll.
  function handleArtifactSaved(updated) {
    setRun((prev) => ({
      ...prev,
      artifacts: (prev.artifacts || []).some((a) => a.id === updated.id)
        ? prev.artifacts.map((a) => (a.id === updated.id ? updated : a))
        : [...(prev.artifacts || []), updated],
    }));
  }

  async function handleDownload() {
    if (!run) return;
    setDownloading(true);
    setError(null);
    try {
      const blob = await studioDocumentsApi.exportRun(run.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${documentType}_run_${run.id}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to download the document.");
    } finally {
      setDownloading(false);
    }
  }

  const loading = sectionsLoading || runLoading || sourceRun === undefined;
  const isActive = run && ACTIVE_STATUSES.has(run.status);
  const isCompleted = run?.status === "completed";
  const isFailed = run?.status === "failed";
  const activeSection = sections.find((s) => s.key === activeKey);
  const activeArtifact = artifactFor(run, activeKey);
  // A dependent document (Design reading Requirements, Technical Design
  // reading Design) can still be generated before its prerequisite finishes —
  // stages are advisory, not gated (see StageRail's own note) — but the
  // studio should say so rather than silently generating from the SOW alone.
  const sourceNotReady = Boolean(sourceDocumentType) && sourceRun?.status !== "completed";

  if (loading) {
    return (
      <div className="space-y-4">
        <SkeletonText lines={2} className="max-w-lg" />
        <SkeletonCard />
      </div>
    );
  }

  return (
    <div>
      {/* Header / generate bar */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <p className="text-xl font-semibold text-slate-900">{title}</p>
          {description && <p className="text-base text-slate-500 mt-0.5">{description}</p>}
          {run && (
            <p className="text-sm text-slate-400 mt-1.5">
              {isActive
                ? run.progress_message || "Generating…"
                : isCompleted
                  ? `${(run.artifacts || []).filter(hasContent).length} of ${sections.length} sections generated`
                  : isFailed
                    ? "Generation failed — see below"
                    : null}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isCompleted && (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setPreviewOpen(true)}
                >
                  <Eye size={13} /> Preview
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={handleDownload}
                  disabled={downloading}
                >
                  {downloading
                    ? <><Loader2 size={13} className="animate-spin" /> Downloading...</>
                    : <><Download size={13} /> Download</>}
                </Button>
              </>
            )}
          <Button
                size="sm"
                variant={isCompleted ? "secondary" : "primary"}
                onClick={handleGenerate}
                disabled={starting || isActive}
              >
                {isActive
                  ? <><Loader2 size={13} className="animate-spin" /> Generating…</>
                  : isCompleted
                    ? <><RefreshCw size={13} /> Regenerate</>
                    : <><Wand2 size={13} /> Generate</>}
              </Button>
            </div>
        </div>

        {error && <Alert variant="danger" className="mb-4">{error}</Alert>}
        {isFailed && run?.error_log && (
          <Alert variant="danger" icon={AlertCircle} title="Generation failed" className="mb-4">
            {run.error_log}
          </Alert>
        )}
        {!run && sourceNotReady && (
          <Alert variant="warning" className="mb-4">
            {sourceDocumentLabel || "The prerequisite document"} hasn&rsquo;t been generated yet — this
            stage can still run, but without it to read from it will lean on the SOW and interaction
            context alone.
          </Alert>
        )}

      {!run ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-center border border-dashed border-gray-200 rounded-xl">
          <Wand2 size={26} className="text-brand-200" />
          <div>
            <p className="text-lg font-semibold text-slate-700 mb-1">Not generated yet</p>
            <p className="text-sm text-slate-400">
              {sourceDocumentType
                ? `Uses ${sourceDocumentLabel || "the prior stage"}, the SOW and any interaction context from Sources.`
                : "Uses the SOW and any interaction context from Sources."}
            </p>
          </div>
        </div>
      ) : (
        <div className="flex gap-5 items-start">
          {/* Section rail */}
          <div className="w-56 shrink-0 bg-white rounded-xl border border-gray-200 overflow-hidden">
            {sections.map((section) => {
              const Icon = ICON_BY_KEY[section.key] || FileText;
              const isGroup = section.kind === "group";
              const groupItems = isGroup ? groupArtifactsFor(run, section.key) : [];
              const artifact = isGroup ? null : artifactFor(run, section.key);
              const active = activeKey === section.key;
              const done = isGroup ? groupItems.length > 0 : hasContent(artifact);
              return (
                <button
                  key={section.key}
                  type="button"
                  onClick={() => setActiveKey(section.key)}
                  className={[
                    "w-full flex items-center gap-2.5 px-4 py-3 text-base text-left border-b border-gray-100 last:border-0 transition-colors",
                    active ? "bg-brand-50 text-brand-700 font-semibold" : "text-slate-600 hover:bg-gray-50",
                  ].join(" ")}
                >
                  <Icon size={13} className="shrink-0" />
                  <span className="flex-1 truncate">{section.title}</span>
                  {isGroup && done
                    ? <span className="text-sm text-emerald-600 font-semibold shrink-0">{groupItems.length}</span>
                    : isActive && !done
                    ? <Loader2 size={11} className="animate-spin text-amber-400 shrink-0" />
                    : done
                    ? <CheckCircle2 size={11} className="text-emerald-500 shrink-0" />
                    : <span className="text-sm text-gray-300 shrink-0">—</span>}
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 bg-white rounded-xl border border-gray-200 p-5">
            {activeSection && (
              <>
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-lg font-semibold text-slate-900">{activeSection.title}</h3>
                  <span className="text-sm text-slate-300">§{activeSection.number}</span>
                </div>
                {activeSection.guidance && (
                  <p className="text-base text-violet-500 bg-violet-50 border border-violet-100 rounded-lg px-3 py-2 mb-3">
                    {activeSection.guidance}
                  </p>
                )}
                {activeSection.kind === "group" ? (
                  <GroupSectionView
                    artifacts={groupArtifactsFor(run, activeSection.key)}
                    runId={run.id}
                    sectionKey={activeSection.key}
                    disabled={isActive}
                    onItemSaved={handleArtifactSaved}
                  />
                ) : !hasContent(activeArtifact) && isActive ? (
                  <SkeletonText lines={4} />
                ) : (
                  <ArtifactEditor
                    runId={run.id}
                    sectionKey={activeSection.key}
                    contentMarkdown={activeArtifact?.content_markdown}
                    isJson={activeSection.kind === "table"}
                    disabled={isActive}
                    onSaved={handleArtifactSaved}
                  >
                    {activeSection.kind === "table" ? (
                      <TableSectionView content={activeArtifact?.content_markdown} columns={activeSection.columns} />
                    ) : activeSection.kind === "diagram" ? (
                      <MermaidView content={activeArtifact?.content_markdown} />
                    ) : (
                      <MarkdownView content={activeArtifact?.content_markdown} />
                    )}
                  </ArtifactEditor>
                )}
              </>
            )}
          </div>
        </div>
      )}
      <DocumentPreviewModal 
          open={previewOpen} 
          onClose={() => setPreviewOpen(false)} 
          run={run} 
          title={title} 
          onDownload={handleDownload}
          isDownloading={downloading}
        />
    </div>
  );
}

      DocumentWorkspace.propTypes = {
        projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      documentType: PropTypes.string.isRequired,
      sowId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      // The document this one reads as input (SPEC-07's ApplicableDocument.depends_on)
      // — its latest completed run is linked as source_run_id on generation.
      sourceDocumentType: PropTypes.string,
      sourceDocumentLabel: PropTypes.string,
};
